// T653 — the single owner of every origin this app depends on that is NOT ours.
//
// THIS IS THE ONLY MODULE ALLOWED TO DECLARE A THIRD-PARTY ORIGIN LITERAL.
// `scripts/maintenance/lint-delivery-host-ssot.mjs` hard-fails on any other one.
//
// ── Why this exists, and why it is separate from `hosts.ts` ───────────────────────
//
// `app/config/hosts.ts` (T424) answers "which host are we?" and is gated. Nothing
// answered "whose host do we depend on?", and that gate cannot see them — its regex
// is literally `thefittclub\.(com|pages\.dev)`. On 2026-09-06 a Spanish ISP block of
// Cloudflare IP RANGES (they block ranges, not domains) emptied the landing while the
// page itself loaded fine, and answering "what does my landing fetch from outside?"
// took an afternoon of grep and curl because no file held the answer. This one does.
//
// ── Why the CSP is generated from here ───────────────────────────────────────────
//
// `entry.server.tsx` used to restate this list by hand, including the Stream customer
// code that `lib/stream/embed.ts` already declared. An origin added to the code
// without remembering to widen the policy is blocked by the browser SILENTLY — no
// build error, no failing test, no server log, just a resource that never arrives.
// Deriving the policy removes that failure mode by construction.
//
// Cloudflare Images delivery is deliberately ABSENT: since T653 those URLs are
// same-origin (`/cdn-cgi/imagedelivery/…`, see `lib/images/variants.ts`), so the only
// Images origin left is the direct-upload endpoint the Back Office posts to.

/**
 * Cloudflare Stream's per-account delivery subdomain code. PUBLIC and account-stable
 * — it appears in every Stream delivery URL — and the same for preview and production
 * because both share one Cloudflare account. It lives here rather than in
 * `lib/stream/embed.ts` so the delivery origin and the CSP that must allow it cannot
 * drift apart; `embed.ts` imports it.
 */
export const STREAM_CUSTOMER_CODE = "n6cgr7phih10ers3";

/** Every non-ours origin the app reaches, with the role that justifies it. */
export const EXTERNAL_ORIGINS = {
  /** Stream delivery: the four decorative background videos and their stills. The one
   *  media dependency T653 could not remove for free — a proxy would bill Function
   *  invocations (see the ticket's `## Out of scope`). */
  streamDelivery: `https://customer-${STREAM_CUSTOMER_CODE}.cloudflarestream.com`,
  /** Direct-upload endpoints the Back Office POSTs media to (`cfDirectUpload.ts`). */
  streamUpload: "https://upload.cloudflarestream.com",
  imagesUpload: "https://upload.imagedelivery.net",
  /**
   * T742 — Cloudflare Images SIGNED delivery, the one Images origin `getPublicUrl` could
   * not make same-origin. Public variants go out as `/cdn-cgi/imagedelivery/…` on whatever
   * host the visitor already reached (T653, see `images.server.ts`); a signed URL still
   * names this host, so it is declared here — and listed in `MEMBER_DELIVERY_ORIGIN_KEYS`
   * below — rather than living as a literal in a service where no gate and no monitor can
   * see it. That was exactly how `R2_S3_ENDPOINT` hid from T730's block monitor until T741
   * found it the hard way.
   */
  imagesDelivery: "https://imagedelivery.net",
  /** Payments. */
  stripeJs: "https://js.stripe.com",
  stripeApi: "https://api.stripe.com",
  stripeCheckout: "https://checkout.stripe.com",
  stripeHooks: "https://hooks.stripe.com",
  /** Observability + product analytics. */
  sentryIngest: "https://*.ingest.de.sentry.io",
  posthog: "https://eu.i.posthog.com",
  // Google Fonts used to be here. T653 self-hosted the faces (`@font-face` at the top
  // of `app/styles/app.css`, files under `public/fonts/`), so the app reaches neither
  // host any more and the CSP below no longer allows either. Removing an origin from
  // this module is what tightens the policy — that is the point of deriving it.
} as const;

/**
 * T742 — which of the origins above hand BYTES TO A MEMBER'S BROWSER.
 *
 * Read by `~/config/delivery-hosts`, the derived view the block monitor's coverage comes
 * from. It is a declaration about this module's own origins, so it stays with them; the view
 * that consumes it introduces nothing.
 *
 * Everything else here is excluded on purpose, and the exclusion is the point (ADR-0025
 * `## Out of scope`). The upload endpoints are reached by the Back Office pushing bytes UP,
 * never by a member pulling them down. Stripe, Sentry and PostHog carry no member bytes at
 * all: an ISP blocking one of them is not a member-facing outage, and an alarm nobody can act
 * on is how a monitor stops being read.
 *
 * `imagesDelivery` joined in slice 2, closing the gap slice 1's audit named out loud: signed
 * image URLs are member bytes on a third-party host, and until the seam existed there was no
 * honest way to pull them in without introducing a host, which ADR-0025 decision 3 forbids.
 */
export const MEMBER_DELIVERY_ORIGIN_KEYS = ["streamDelivery", "imagesDelivery"] as const;

const O = EXTERNAL_ORIGINS;

/**
 * The Content-Security-Policy, assembled from the declarations above.
 *
 * `img-src` / `media-src` stay at the broad `https:` they have always had rather than
 * being narrowed here: narrowing them is a security change, and this ticket's contract
 * is that the generated policy is byte-identical to the hand-written one it replaces
 * (pinned by `tests/unit/infra/653-csp-derived-from-external-origins.test.ts`). Narrow
 * them in a ticket that can measure what breaks.
 *
 * `script-src` still carries `'unsafe-inline'` — see the nonce TODO in
 * `entry.server.tsx`, unchanged by T653.
 */
export const CONTENT_SECURITY_POLICY = [
  "default-src 'self'",
  "base-uri 'self'",
  "object-src 'none'",
  "frame-ancestors 'none'",
  `form-action 'self' ${O.stripeCheckout}`,
  `script-src 'self' 'unsafe-inline' ${O.stripeJs}`,
  "style-src 'self' 'unsafe-inline'",
  "font-src 'self'",
  // `blob:` in img-src: the avatar crop UI (T278) previews the picked photo from a
  // client-created object URL before upload. blob: URLs are same-origin and
  // app-created; this does not widen exposure to remote image sources.
  "img-src 'self' data: blob: https:",
  "media-src 'self' blob: https:",
  `connect-src 'self' ${O.sentryIngest} ${O.stripeApi} ${O.stripeCheckout} ${O.posthog} ${O.streamUpload} ${O.imagesUpload}`,
  // `'self'` was added on 2026-09-12 for the phase-material viewer, which then showed a PDF in a
  // same-origin `<iframe>`: a present `frame-src` is the ONLY list a browser consults for a frame
  // (`default-src 'self'` does not apply), so without it the card opened, the title rendered and
  // the document was silently blocked. **T731 removed that iframe** — pdf.js paints the pages into
  // canvases, because iOS never rendered a framed PDF anyway — so no surface frames our own origin
  // today. The entry stays because dropping it is a policy TIGHTENING, not a cleanup: it has to
  // move in lockstep with `public/_headers` and the two tests that pin this string, in a change
  // that can say what it checked. Recorded here so the next reader knows it is deliberate, not stale.
  `frame-src 'self' ${O.stripeJs} ${O.stripeHooks} ${O.streamDelivery}`,
].join("; ");
